import {
  DISPATCH_STATES,
  DISPATCHABLE_WAITLIST_TYPES,
  PUBLIC_WEBSITE_BASE_URL,
  WAITLIST_TYPES
} from "./constants";

/** Whether a waitlist type runs dispatches (drives dispatch/skip UI visibility). */
export const isDispatchableType = (type) => DISPATCHABLE_WAITLIST_TYPES.includes(type);

/**
 * Absolute public join URL for a waitlist, shareable with users. Mirrors the
 * frontend public waitlist route (/waitlist/:uniquePublicId).
 * @param {string} uniquePublicId - The waitlist's slug.
 * @returns {string}
 */
export const buildWaitlistJoinUrl = (uniquePublicId) =>
  `${PUBLIC_WEBSITE_BASE_URL}/waitlist/${uniquePublicId}`;

/**
 * Safely copies text to the clipboard with fallback for non-secure HTTP contexts.
 * @param {string} text - Text to copy
 * @param {function} [onSuccess] - Callback executed when copy succeeds
 */
export const copyToClipboard = (text, onSuccess = () => {}) => {
  if (!text) {
    return;
  }

  const handleSuccess = () => {
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  };

  if (navigator?.clipboard?.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(handleSuccess)
      .catch(() => fallbackCopyToClipboard(text, handleSuccess));
  } else {
    fallbackCopyToClipboard(text, handleSuccess);
  }
};

/**
 * Progress percentage for a dispatch — used by the Progress bar in the dispatch modal
 * and the "Last Dispatch" column on the list view.
 * @param {{ processedCount?: number, skippedCount?: number, targetedEntriesCount?: number }} dispatch
 * @returns {number}
 */
export const computeDispatchProgressPercent = (dispatch) => {
  const { processedCount = 0, skippedCount = 0, targetedEntriesCount = 0 } = dispatch || {};
  if (!targetedEntriesCount) {
    return 0;
  }
  return Math.min(100, Math.round(((processedCount + skippedCount) / targetedEntriesCount) * 100));
};

/**
 * Antd Progress `status` for a dispatch, based on its state.
 * @param {string} state
 * @returns {"active"|"exception"|"success"|"normal"}
 */
export const dispatchProgressStatus = (state) => {
  if (state === DISPATCH_STATES.FAILED) {
    return "exception";
  }
  if (state === DISPATCH_STATES.COMPLETED) {
    return "success";
  }
  if (state === DISPATCH_STATES.IN_PROGRESS) {
    return "active";
  }
  return "normal";
};

/**
 * Maps a config's type-specific target field to its raw value for form pre-fill on edit.
 * @param {{ type: string, displayCouponCode?: string, accessKey?: string, mailingListId?: string }} waitlistConfig
 * @returns {string|undefined}
 */
export const getTypeTargetFieldValue = (waitlistConfig) => {
  const { type, displayCouponCode, accessKey } = waitlistConfig || {};
  switch (type) {
    case WAITLIST_TYPES.DISCOUNT_ALLOWLIST:
      return displayCouponCode;
    case WAITLIST_TYPES.USER_ACCESS_GRANT:
      return accessKey;
    default:
      return undefined;
  }
};

const fallbackCopyToClipboard = (text, onSuccess) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand("copy");
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  } catch (err) {
    console.error("Fallback copy failed", err);
  }
  document.body.removeChild(textArea);
};
